import React, { useState, useEffect, useRef } from "react";
import BackButton from "../../components/buttons/BackButton";
import MakeConnectionButton from "../../components/buttons/MakeConnectionButton";
import ShareViaEmailButton from "../../components/buttons/ShareViaEmailButton";
import ClearHistoryButton from "../../components/buttons/ClearHistoryButton";
import MakeConnectionModal, {
  MakeConnectionModalHandle,
} from "../../components/modals/MakeConnectionModal";
import SendEmailModal from "../../components/modals/SendEmailModal";
import ArtworkImageLoader from "../../components/ArtworkImageLoader";
import { useStore } from "../../store/store";
import { ArtworkWithTexture } from "../../types";
import { useLocation } from "wouter";
import { parse } from "raji";
import { animated, useTransition } from "@react-spring/web";

const HistoryView: React.FC = () => {
  const [, navigate] = useLocation();
  const [viewHistory, setViewHistory] = useState<ArtworkWithTexture[]>([]);
  const { setArtworkInFront } = useStore();
  const [isMakingArtworkConnection, setIsMakingArtworkConnection] =
    useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [selectedArtworkIds, setSelectedArtworkIds] = useState<string[]>([]);
  const artworkConnectionModalRef = useRef<MakeConnectionModalHandle>(null);
  const sendEmailModalRef = useRef<any>(null);

  const loadViewHistory = async () => {
    const historyData = localStorage.getItem("artworkViewHistory");
    if (historyData) {
      const parsedHistory: ArtworkWithTexture[] = await parse(historyData);
      setViewHistory(parsedHistory.reverse());
    }
  };

  const back = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("/collection");
    }
  };

  const openArtworkConnectionModal = () => {
    setIsMakingArtworkConnection(true);
  };

  const closeConnectionModal = () => {
    setSelectedArtworkIds([]);
    setIsMakingArtworkConnection(false);
  };

  const openEmailModal = () => {
    setEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    setSelectedArtworkIds([]);
    setEmailModalOpen(false);
    sendEmailModalRef.current.resetForm();
  };

  const sendEmail = async ({
    name,
    email,
  }: {
    name: string;
    email: string;
  }) => {
    setSendEmailLoading(true);
    const shareUrl = "https://admin.collectieconnectie.nl/api/v1/share";
    const data = {
      name,
      email,
      artworks: selectedArtworkIds,
    };

    try {
      const response = await fetch(shareUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      await response.json();
      setSendEmailLoading(false);
      closeEmailModal();
    } catch (err) {
      setSendEmailLoading(false);
    }
  };

  const selectAllArtworks = () => {
    const allIds = viewHistory.map((artwork) => artwork.uuid);
    setSelectedArtworkIds(allIds);
  };

  const selectArtwork = (selectedArtwork: ArtworkWithTexture) => {
    if (isMakingArtworkConnection) {
      if (selectedArtworkIds.length < 2) {
        setSelectedArtworkIds([...selectedArtworkIds, selectedArtwork.uuid]);
      }
      artworkConnectionModalRef.current?.selectArtwork(selectedArtwork);
    }
    if (isSendingEmail()) {
      if (artworkIsSelected(selectedArtwork.uuid)) {
        removeArtworkId(selectedArtwork.uuid);
      } else {
        setSelectedArtworkIds([...selectedArtworkIds, selectedArtwork.uuid]);
      }
    }
  };

  const removeArtworkId = (uuid: string) => {
    setSelectedArtworkIds(selectedArtworkIds.filter((id) => id !== uuid));
  };

  const artworkIsSelected = (uuid: string) => {
    return isSelectingArtwork() && selectedArtworkIds.includes(uuid);
  };

  const isSelectingArtwork = () => {
    return isMakingArtworkConnection || isSendingEmail();
  };

  const isSendingEmail = () => {
    return emailModalOpen;
  };

  useEffect(() => {
    setArtworkInFront(true);
    loadViewHistory();
  }, [setArtworkInFront]);

  const transitions = useTransition(true, {
    from: {
      opacity: 0,
      backdropFilter: "blur(0px)",
      WebkitBackdropFilter: "blur(0px)",
    },
    enter: {
      opacity: 1,
      backdropFilter: "blur(30px)",
      WebkitBackdropFilter: "blur(30px)",
    },
    leave: {
      backdropFilter: "blur(0px)",
      opacity: 0,
      WebkitBackdropFilter: "blur(0px)",
    },
    config: { tension: 280, friction: 60 },
  });
  return transitions(
    (styles, item) =>
      item && (
        <animated.div
          style={{
            ...styles,
          }}
          className={
            "z-20 min-h-screen md:overflow-hidden md:h-full w-full absolute top-0 left-0 overflow-y-scroll no-scrollbar"
          }
        >
          <div id="History">
            {/* Connection Modals */}
            {isMakingArtworkConnection && (
              <MakeConnectionModal
                ref={artworkConnectionModalRef}
                onClose={closeConnectionModal}
                removeArtworkId={removeArtworkId}
              />
            )}

            {/* Email Modals */}
            {emailModalOpen && (
              <SendEmailModal
                ref={sendEmailModalRef}
                canSendEmail={selectedArtworkIds.length > 0}
                loading={sendEmailLoading}
                onClose={closeEmailModal}
                onSendEmail={sendEmail}
                onSelectAll={selectAllArtworks}
              />
            )}

            {/* Actions */}
            <div className="pt-8 px-4 fixed z-10">
              <BackButton onClick={back} />
            </div>

            {/* Title Mobile */}
            <div className="px-4 pt-20 md:pt-4 md:hidden">
              <h1 className="text-4xl text-white">Geschiedenis</h1>
              <span className="inline-block text-gray-400">
                <span>{viewHistory.length} objecten</span>
              </span>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 fixed flex gap-3 px-4 overflow-x-auto hide-scrollbars w-full z-10 md:absolute md:right-0 md:flex-col md:items-end md:w-auto md:pt-8">
              <MakeConnectionButton onClick={openArtworkConnectionModal} />
              <ShareViaEmailButton onClick={openEmailModal} />
              <ClearHistoryButton />
            </div>

            {/* Artwork Listing */}
            <div className="mx-auto px-4 py-4 md:py-16 relative no-scrollbar md:overflow-scroll md:min-h-screen md:h-screen md:pr-56 md:pl-24 xl:container">
              {/* Title Desktop */}
              <h1 className="text-4xl text-white hidden md:block">
                Geschiedenis
              </h1>
              <span className="text-gray-400 hidden md:inline-block">
                <span>{viewHistory.length} objecten</span>
              </span>

              {/* Artwork List */}
              <ul className="sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 md:gap-x-4 gap-y-4 pt-8">
                {viewHistory.map((artwork) => (
                  <li
                    key={artwork.uuid}
                    className={`relative pb-5 select-none ${
                      isSelectingArtwork() ? "cursor-pointer" : ""
                    }`}
                    onClick={() => selectArtwork(artwork)}
                  >
                    <div className="group block w-full aspect-none relative">
                      {artworkIsSelected(artwork.uuid) && (
                        <div className="absolute w-8 h-8 border-2 rounded-full bg-black bg-opacity-20 backdrop--tinted -left-2 -top-2 flex pt-2 justify-center items-center z-10">
                          <span className="d-icon d-check text-white text-xl cursor-pointer group-hover:hidden"></span>
                          <span
                            className="d-icon d-close text-white text-xl hidden group-hover:block cursor-pointer"
                            onClick={(event) => {
                              event.stopPropagation(); // Prevents the parent onClick
                              removeArtworkId(artwork.uuid);
                              artworkConnectionModalRef.current?.clearArtworkById(
                                artwork.uuid,
                              );
                            }}
                          ></span>
                        </div>
                      )}
                      <ArtworkImageLoader
                        className="w-full object-contain object-left max-h-[28rem] pointer-events-none"
                        artwork={artwork}
                        lowResImageOnly={true}
                      />
                    </div>
                    <p className="mt-3 block text-lg leading-none text-white truncate pointer-events-none">
                      {artwork.title}
                    </p>
                    <p className="block text-sm font-bold text-white pointer-events-none">
                      {artwork.author.name}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </animated.div>
      ),
  );
};

export default HistoryView;
